import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import Button from '../../ui/Button';
import { Dialog } from './AccountBits';
import nurseryService from '../../../services/nursery';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { useOrgTimezone, getTodayInTz } from '../../../utils/timezone';
import { money, pct, round2 } from '../../../utils/nurseryMoney';
import type { NurseryCustomer } from '../../../types/nursery';
import {
  REBATE_KIND_LABELS,
  REBATE_SETTLEMENT_LABELS,
  type CreateRebateAgreementPayload,
  type RebateAgreement,
  type RebateBandMode,
  type RebateKind,
  type RebatePeriod,
  type RebateSettlement,
} from '../../../types/nurseryAccounts';

/**
 * A new rebate agreement. There is no edit form on purpose: an agreement is
 * ended and replaced, never changed, so the settlement of an old period can
 * always be worked out again from the agreement it was made under.
 *
 * What each kind needs mirrors `agreementRefusal` in the server's
 * nurseryRebateService; the server stays the authority and its refusal is
 * shown in the dialog.
 */

export interface RebateAgreementDialogProps {
  initialCustomerId?: number;
  onClose: () => void;
  onCreated: (a: RebateAgreement) => void;
}

interface PartRow { key: number; label: string; rate: string; amount: string; account: string }
interface BandRow { key: number; threshold: string; rate: string }

const KINDS: RebateKind[] = ['flat_pct', 'volume_tiered', 'fixed_amount', 'settlement_discount'];

/** Which ways each kind can be settled; the first is the default. */
const SETTLEMENTS: Record<RebateKind, RebateSettlement[]> = {
  flat_pct: ['per_invoice', 'credit_note', 'billed_to_us'],
  volume_tiered: ['per_invoice', 'credit_note', 'billed_to_us'],
  fixed_amount: ['credit_note', 'billed_to_us'],
  settlement_discount: [],
};

/** Months of the year, 1 to 12; names come from the translation file. */
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const INPUT = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';
const LABEL = 'block text-sm font-medium text-neutral-700 mb-1';
const SMALL_LABEL = 'block text-xs font-medium text-neutral-600 mb-1';

/** A typed number, or null when the box is blank or not a number. */
const typed = (s: string): number | null => {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

export function RebateAgreementDialog({ initialCustomerId, onClose, onCreated }: RebateAgreementDialogProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'RebateAgreementDialog' });
  const tz = useOrgTimezone();
  const nextKey = useRef(10);
  const newKey = () => { nextKey.current += 1; return nextKey.current; };

  const [customers, setCustomers] = useState<NurseryCustomer[]>([]);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<number>(initialCustomerId ?? 0);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<RebateKind>('flat_pct');
  const [settlement, setSettlement] = useState<RebateSettlement>('per_invoice');
  const [parts, setParts] = useState<PartRow[]>([
    { key: 1, label: 'Rebate', rate: '', amount: '', account: '' },
    { key: 2, label: 'Marketing', rate: '', amount: '', account: '' },
  ]);
  const [bands, setBands] = useState<BandRow[]>([{ key: 3, threshold: '0', rate: '' }]);
  const [bandMode, setBandMode] = useState<RebateBandMode>('retrospective');
  const [accountCode, setAccountCode] = useState('');
  const [discountRate, setDiscountRate] = useState('');
  const [discountDays, setDiscountDays] = useState('');
  const [period, setPeriod] = useState<RebatePeriod>('year');
  const [startMonth, setStartMonth] = useState(7);
  const [includeDelivery, setIncludeDelivery] = useState(false);
  const [from, setFrom] = useState(() => getTodayInTz(tz));
  const [to, setTo] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await nurseryService.getCustomers({ top_level: true, active: true });
        if (!live) return;
        if (res.success) setCustomers(res.data || []);
        else setCustomersError(errorMessage(res, t('loadCustomersFailed')));
      } catch (e) {
        if (live) setCustomersError(errorMessage(e, t('loadCustomersFailed')));
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loads once; t only supplies the fallback message
  }, []);

  const changeKind = (k: RebateKind) => {
    setKind(k);
    setError(null);
    const options = SETTLEMENTS[k];
    if (options.length && !options.includes(settlement)) setSettlement(options[0]);
    if (k === 'fixed_amount' && parts.every((p) => !p.amount)) {
      setParts([{ key: newKey(), label: 'Marketing contribution', rate: '', amount: '', account: '' }]);
    }
    if (k === 'flat_pct' && parts.every((p) => !p.rate)) {
      setParts([
        { key: newKey(), label: 'Rebate', rate: '', amount: '', account: '' },
        { key: newKey(), label: 'Marketing', rate: '', amount: '', account: '' },
      ]);
    }
  };

  const setPart = (key: number, patch: Partial<PartRow>) =>
    setParts((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const setBand = (key: number, patch: Partial<BandRow>) =>
    setBands((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const totalRate = round2(parts.reduce((n, p) => n + (typed(p.rate) ?? 0), 0));
  const totalAmount = round2(parts.reduce((n, p) => n + (typed(p.amount) ?? 0), 0));

  /** The payload, or the sentence saying what is still missing. */
  const build = (): { payload?: CreateRebateAgreementPayload; problem?: string } => {
    if (!customerId) return { problem: t('problem.customer') };
    if (name.trim().length < 2) return { problem: t('problem.name') };

    let components: CreateRebateAgreementPayload['components'] = [];
    let bandList: CreateRebateAgreementPayload['bands'] = [];

    if (kind === 'flat_pct' || kind === 'fixed_amount') {
      const field = kind === 'flat_pct' ? 'rate' : 'amount';
      // A row left wholly blank is a row nobody wanted.
      const used = parts.filter((p) => p[field].trim() !== '' || p.account.trim() !== '');
      for (const p of used) {
        const v = typed(p[field]);
        if (v === null || v < 0) {
          return { problem: kind === 'flat_pct' ? t('problem.partRate') : t('problem.partAmount') };
        }
        if (kind === 'flat_pct' && v > 100) return { problem: t('problem.rateOver100') };
        if (!p.label.trim()) return { problem: t('problem.partLabel') };
      }
      if (!used.some((p) => (typed(p[field]) ?? 0) > 0)) {
        return {
          problem: kind === 'flat_pct'
            ? t('problem.anyRate')
            : t('problem.anyAmount'),
        };
      }
      components = used.map((p) => ({
        label: p.label.trim(),
        rate_pct: kind === 'flat_pct' ? typed(p.rate) : null,
        fixed_amount: kind === 'fixed_amount' ? typed(p.amount) : null,
        account_code: p.account.trim() || null,
      }));
    }

    if (kind === 'volume_tiered') {
      const used = bands.filter((b) => b.rate.trim() !== '');
      if (used.length === 0) return { problem: t('problem.anyBand') };
      const seen = new Set<number>();
      for (const b of used) {
        const threshold = typed(b.threshold);
        const rate = typed(b.rate);
        if (threshold === null || threshold < 0) return { problem: t('problem.bandThreshold') };
        if (rate === null || rate < 0 || rate > 100) return { problem: t('problem.bandRate') };
        if (seen.has(threshold)) return { problem: t('problem.bandDuplicate', { amount: money(threshold) }) };
        seen.add(threshold);
        bandList.push({ threshold, rate_pct: rate });
      }
      bandList = bandList.sort((a, b) => a.threshold - b.threshold);
    }

    let days: number | null = null;
    if (kind === 'settlement_discount') {
      const rate = typed(discountRate);
      if (rate === null || rate <= 0 || rate > 100) return { problem: t('problem.discountRate') };
      days = typed(discountDays);
      if (days === null || !Number.isInteger(days) || days < 1 || days > 365) {
        return { problem: t('problem.discountDays') };
      }
      components = [{ label: 'Settlement discount', rate_pct: rate, fixed_amount: null, account_code: accountCode.trim() || null }];
    }

    if (!from) return { problem: t('problem.from') };
    if (to && to < from) return { problem: t('problem.toBeforeFrom') };

    return {
      payload: {
        customer_id: customerId,
        name: name.trim(),
        kind,
        ...(kind === 'settlement_discount' ? {} : { settlement }),
        period,
        period_start_month: startMonth,
        band_mode: bandMode,
        include_delivery: includeDelivery,
        discount_days: days,
        account_code: kind === 'volume_tiered' || kind === 'settlement_discount' ? accountCode.trim() || null : null,
        effective_from: from,
        effective_to: to || null,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        components,
        bands: bandList,
      },
    };
  };

  const submit = async () => {
    const { payload, problem } = build();
    if (!payload) {
      setError(problem ?? t('problem.missing'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.createRebateAgreement(payload);
      if (res.success && res.data) onCreated(res.data);
      else {
        setError(errorMessage(res, t('saveFailed')));
        setBusy(false);
      }
    } catch (e) {
      setError(errorMessage(e, t('saveFailed')));
      setBusy(false);
    }
  };

  const settlements = SETTLEMENTS[kind];
  const isRate = kind === 'flat_pct';

  return (
    <Dialog
      title={t('title')}
      subtitle={t('subtitle')}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={submit} disabled={busy}>{busy ? t('saving') : t('save')}</Button>
        </>
      }
    >
      <div>
        <label htmlFor="ra-customer" className={LABEL}>{t('customer')}</label>
        <select id="ra-customer" value={customerId || ''} onChange={(e) => setCustomerId(Number(e.target.value) || 0)} className={INPUT}>
          <option value="">{t('chooseCustomer')}</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>{c.name}{c.customer_code ? ` (${c.customer_code})` : ''}</option>
          ))}
        </select>
        <p className="mt-1 text-xs text-neutral-500">{t('customerHelp')}</p>
        {customersError && <p className="mt-1 text-xs text-red-700 break-words">{customersError}</p>}
      </div>

      <div>
        <label htmlFor="ra-name" className={LABEL}>{t('name')}</label>
        <input id="ra-name" type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={255}
          placeholder={t('namePlaceholder')} className={INPUT} />
      </div>

      <div>
        <label htmlFor="ra-kind" className={LABEL}>{t('kind')}</label>
        <select id="ra-kind" value={kind} onChange={(e) => changeKind(e.target.value as RebateKind)} className={INPUT}>
          {KINDS.map((k) => <option key={k} value={k}>{t(`rebateKind.${k}`, { defaultValue: REBATE_KIND_LABELS[k] })}</option>)}
        </select>
      </div>

      {settlements.length > 0 && (
        <fieldset>
          <legend className={LABEL}>{t('settlement')}</legend>
          <div className="space-y-2">
            {settlements.map((s) => (
              <label key={s} htmlFor={`ra-settlement-${s}`} className="flex items-start gap-2 text-sm text-neutral-800">
                <input id={`ra-settlement-${s}`} type="radio" name="ra-settlement" className="mt-1 flex-shrink-0"
                  checked={settlement === s} onChange={() => setSettlement(s)} />
                <span className="min-w-0 break-words">
                  <span className="font-medium">{t(`rebateSettlement.${s}`, { defaultValue: REBATE_SETTLEMENT_LABELS[s] })}</span>
                  <span className="block text-xs text-neutral-500">{t(`settlementHelp.${s}`)}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {(kind === 'flat_pct' || kind === 'fixed_amount') && (
        <div>
          <p className={LABEL}>{t('parts')}</p>
          <ul className="space-y-2">
            {parts.map((p) => (
              <li key={p.key} className="border border-neutral-200 rounded-lg p-3 grid grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1fr)_7rem_8rem_auto] sm:items-end">
                <div className="col-span-2 sm:col-span-1 min-w-0">
                  <label htmlFor={`ra-part-label-${p.key}`} className={SMALL_LABEL}>{t('partLabel')}</label>
                  <input id={`ra-part-label-${p.key}`} type="text" value={p.label} maxLength={100}
                    onChange={(e) => setPart(p.key, { label: e.target.value })} className={INPUT} />
                </div>
                <div className="min-w-0">
                  <label htmlFor={`ra-part-value-${p.key}`} className={SMALL_LABEL}>{isRate ? t('ratePct') : t('amountPerPeriod')}</label>
                  <input id={`ra-part-value-${p.key}`} type="number" inputMode="decimal" min={0} step="0.01"
                    value={isRate ? p.rate : p.amount}
                    onChange={(e) => setPart(p.key, isRate ? { rate: e.target.value } : { amount: e.target.value })}
                    className={INPUT} />
                </div>
                <div className="min-w-0">
                  <label htmlFor={`ra-part-account-${p.key}`} className={SMALL_LABEL}>{t('accountCode')}</label>
                  <input id={`ra-part-account-${p.key}`} type="text" value={p.account} maxLength={20} placeholder={t('optional')}
                    onChange={(e) => setPart(p.key, { account: e.target.value })} className={INPUT} />
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  <button type="button" onClick={() => setParts((rows) => rows.filter((r) => r.key !== p.key))}
                    disabled={parts.length === 1} aria-label={t('removePart', { label: p.label || t('thisPart') })}
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-2 text-sm text-neutral-600 hover:bg-neutral-100 disabled:opacity-40">
                    <Trash2 className="h-4 w-4" /> <span className="sm:hidden">{t('remove')}</span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <Button variant="outline" size="sm" disabled={parts.length >= 10}
              onClick={() => setParts((rows) => [...rows, { key: newKey(), label: '', rate: '', amount: '', account: '' }])}>
              <Plus className="h-4 w-4 mr-1" /> {t('addPart')}
            </Button>
            <p className="text-sm font-medium text-neutral-800">
              {isRate ? t('totalRate', { rate: pct(totalRate) }) : t('totalAmount', { amount: money(totalAmount) })}
            </p>
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            {isRate ? t('partsHelpRate') : t('partsHelpAmount')}
            {' '}{t('partsHelpAccount')}
          </p>
        </div>
      )}

      {kind === 'volume_tiered' && (
        <>
          <div>
            <p className={LABEL}>{t('bands')}</p>
            <ul className="space-y-2">
              {bands.map((b) => (
                <li key={b.key} className="border border-neutral-200 rounded-lg p-3 grid grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
                  <div className="min-w-0">
                    <label htmlFor={`ra-band-from-${b.key}`} className={SMALL_LABEL}>{t('spendFrom')}</label>
                    <input id={`ra-band-from-${b.key}`} type="number" inputMode="decimal" min={0} step="1" value={b.threshold}
                      onChange={(e) => setBand(b.key, { threshold: e.target.value })} className={INPUT} />
                  </div>
                  <div className="min-w-0">
                    <label htmlFor={`ra-band-rate-${b.key}`} className={SMALL_LABEL}>{t('ratePct')}</label>
                    <input id={`ra-band-rate-${b.key}`} type="number" inputMode="decimal" min={0} max={100} step="0.01" value={b.rate}
                      onChange={(e) => setBand(b.key, { rate: e.target.value })} className={INPUT} />
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex justify-end">
                    <button type="button" onClick={() => setBands((rows) => rows.filter((r) => r.key !== b.key))}
                      disabled={bands.length === 1} aria-label={t('removeBand')}
                      className="inline-flex items-center gap-1 rounded-lg px-2 py-2 text-sm text-neutral-600 hover:bg-neutral-100 disabled:opacity-40">
                      <Trash2 className="h-4 w-4" /> <span className="sm:hidden">{t('remove')}</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-2">
              <Button variant="outline" size="sm" disabled={bands.length >= 20}
                onClick={() => setBands((rows) => [...rows, { key: newKey(), threshold: '', rate: '' }])}>
                <Plus className="h-4 w-4 mr-1" /> {t('addBand')}
              </Button>
            </div>
            <p className="mt-2 text-xs text-neutral-500">{t('bandsHelp')}</p>
          </div>

          <fieldset>
            <legend className={LABEL}>{t('bandMode')}</legend>
            <div className="space-y-2">
              {(['retrospective', 'marginal'] as RebateBandMode[]).map((m) => (
                <label key={m} htmlFor={`ra-band-mode-${m}`} className="flex items-start gap-2 text-sm text-neutral-800">
                  <input id={`ra-band-mode-${m}`} type="radio" name="ra-band-mode" className="mt-1 flex-shrink-0"
                    checked={bandMode === m} onChange={() => setBandMode(m)} />
                  <span className="min-w-0 break-words">
                    <span className="font-medium">{t(`bandModeLabel.${m}`)}</span>
                    <span className="block text-xs text-neutral-500">{t(`bandModeHelp.${m}`)}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {settlement === 'per_invoice' && (
            <p className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-900 break-words">
              {t('perInvoiceBands')}
            </p>
          )}
        </>
      )}

      {kind === 'settlement_discount' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="ra-discount-rate" className={LABEL}>{t('discountPct')}</label>
            <input id="ra-discount-rate" type="number" inputMode="decimal" min={0} max={100} step="0.01" value={discountRate}
              onChange={(e) => setDiscountRate(e.target.value)} className={INPUT} />
          </div>
          <div>
            <label htmlFor="ra-discount-days" className={LABEL}>{t('discountDays')}</label>
            <input id="ra-discount-days" type="number" inputMode="numeric" min={1} max={365} step="1" value={discountDays}
              onChange={(e) => setDiscountDays(e.target.value)} className={INPUT} />
          </div>
          <p className="sm:col-span-2 text-xs text-neutral-500">
            {t('discountHelp')}
          </p>
        </div>
      )}

      {(kind === 'volume_tiered' || kind === 'settlement_discount') && (
        <div>
          <label htmlFor="ra-account" className={LABEL}>{t('accountCode')}</label>
          <input id="ra-account" type="text" value={accountCode} maxLength={20} placeholder={t('optional')}
            onChange={(e) => setAccountCode(e.target.value)} className={INPUT} />
          <p className="mt-1 text-xs text-neutral-500">{t('accountHelp')}</p>
        </div>
      )}

      {kind !== 'settlement_discount' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="ra-period" className={LABEL}>{t('period')}</label>
            <select id="ra-period" value={period} onChange={(e) => setPeriod(e.target.value as RebatePeriod)} className={INPUT}>
              {(['month', 'quarter', 'year'] as RebatePeriod[]).map((p) => <option key={p} value={p}>{t(`periodLabel.${p}`)}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="ra-start-month" className={LABEL}>{t('startMonth')}</label>
            <select id="ra-start-month" value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))} className={INPUT}>
              {MONTHS.map((m) => <option key={m} value={m}>{t(`month.${m}`)}</option>)}
            </select>
          </div>
        </div>
      )}

      {kind !== 'settlement_discount' && (
        <label htmlFor="ra-delivery" className="flex items-start gap-2 text-sm text-neutral-800">
          <input id="ra-delivery" type="checkbox" className="mt-1 flex-shrink-0" checked={includeDelivery}
            onChange={(e) => setIncludeDelivery(e.target.checked)} />
          <span className="min-w-0 break-words">{t('includeDelivery')}</span>
        </label>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="ra-from" className={LABEL}>{t('from')}</label>
          <input id="ra-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={INPUT} />
        </div>
        <div>
          <label htmlFor="ra-to" className={LABEL}>{t('to')}</label>
          <input id="ra-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={INPUT} />
        </div>
      </div>

      <div>
        <label htmlFor="ra-notes" className={LABEL}>{t('notes')}</label>
        <textarea id="ra-notes" rows={2} value={notes} maxLength={2000} onChange={(e) => setNotes(e.target.value)} className={INPUT} />
      </div>

      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>
      )}
    </Dialog>
  );
}

export default RebateAgreementDialog;
